import { useEffect, useState } from "react";

import {
  api,
  type CalendarColor,
  type EnergyLevel,
  type Label as LabelData,
  type LookBack,
  type LookBackActivity,
  type Resolution,
} from "@/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ErrorAlert } from "@/components/error-alert";
import { PageSkeleton } from "@/components/page-skeleton";
import { formatMinutes } from "@/lib/color";
import { labelHex, typeLabel } from "@/lib/domain";
import { cn } from "@/lib/utils";

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

const steps = [
  "Look back",
  "Second opinion",
  "Score yesterday",
  "Energy for today",
] as const;

function when(a: LookBackActivity): string {
  const s = new Date(a.start_at);
  const e = new Date(a.end_at);
  const day = s.toLocaleDateString([], {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  const t = (d: Date) =>
    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return `${day}, ${t(s)}–${t(e)}`;
}

/** Proposed Day Score: share of Done on 0–10 (mirrors the server rule). */
function propose(done: number, total: number): number | null {
  if (total === 0) return null;
  return Math.round((done / total) * 10);
}

type Tally = Record<Resolution, number>;

/**
 * The Planning Session wizard, opened from Today. Steps: classify every
 * past unresolved Activity → assign yesterday's Day Score with an optional
 * note → choose today's Energy Level → close and refresh Today.
 */
export function PlanningWizard({
  labels,
  colors,
  energyLevels,
  todayEnergyLevelId,
  onClose,
  onFinished,
}: {
  labels: LabelData[];
  colors: CalendarColor[];
  energyLevels: EnergyLevel[];
  todayEnergyLevelId: string | null;
  onClose: () => void;
  onFinished: () => Promise<void>;
}) {
  const [step, setStep] = useState(0);
  const [lookBack, setLookBack] = useState<LookBack | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tally, setTally] = useState<Tally>({ done: 0, carry: 0, drop: 0 });

  useEffect(() => {
    (async () => {
      try {
        setLookBack(await api.lookBack());
      } catch (err) {
        setError(messageOf(err, "could not start the Planning Session"));
      }
    })();
  }, []);

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-foreground/40 p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="planning-title"
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-2xl rounded-2xl border border-border bg-card p-5 shadow-xl sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="planning-title" className="text-lg font-semibold">
              Planning Session
            </h2>
            <ol className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs">
              {steps.map((name, i) => (
                <li
                  key={name}
                  className={cn(
                    "flex items-center gap-1.5",
                    i === step
                      ? "font-medium text-foreground"
                      : "text-muted-foreground",
                  )}
                  aria-current={i === step ? "step" : undefined}
                >
                  <span
                    className={cn(
                      "inline-flex h-4 w-4 items-center justify-center rounded-full text-[10px]",
                      i < step
                        ? "bg-primary text-primary-foreground"
                        : i === step
                          ? "border border-primary text-primary"
                          : "border border-border",
                    )}
                  >
                    {i + 1}
                  </span>
                  {name}
                </li>
              ))}
            </ol>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
        </div>

        <div className="mt-5">
          {error && <ErrorAlert message={error} />}
          {!error && !lookBack && <PageSkeleton />}
          {lookBack && step === 0 && (
            <LookBackStep
              lookBack={lookBack}
              labels={labels}
              colors={colors}
              onClassified={(res) =>
                setTally((t) => ({ ...t, [res]: t[res] + 1 }))
              }
              onNext={() => setStep(1)}
            />
          )}
          {lookBack && step === 1 && (
            <SecondOpinionStep
              lookBack={lookBack}
              onRefresh={async () => setLookBack(await api.lookBack())}
              onBack={() => setStep(0)}
              onNext={() => setStep(2)}
            />
          )}
          {lookBack && step === 2 && (
            <ScoreStep
              key={lookBack.day_plan.assessment?.assessed_at ?? "none"}
              lookBack={lookBack}
              tally={tally}
              onBack={() => setStep(1)}
              onNext={() => setStep(3)}
            />
          )}
          {lookBack && step === 3 && (
            <EnergyStep
              today={lookBack.today}
              energyLevels={energyLevels}
              initial={todayEnergyLevelId}
              onBack={() => setStep(2)}
              onFinished={onFinished}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// --- Step 1: look back ---

function LookBackStep({
  lookBack,
  labels,
  colors,
  onClassified,
  onNext,
}: {
  lookBack: LookBack;
  labels: LabelData[];
  colors: CalendarColor[];
  onClassified: (res: Resolution) => void;
  onNext: () => void;
}) {
  const [pending, setPending] = useState<LookBackActivity[]>(lookBack.items);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [depleted, setDepleted] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const labelById = new Map(labels.map((l) => [l.id, l]));

  async function classify(item: LookBackActivity, res: Resolution) {
    setBusyId(item.id);
    setError(null);
    try {
      const result = await api.resolveActivity(item.id, res);
      setPending((p) => p.filter((x) => x.id !== item.id));
      onClassified(res);
      if (result.carry_error) setNotice(result.carry_error);
      if (result.project_depleted && result.template) {
        setDepleted({ id: result.template.id, title: result.template.title });
      }
    } catch (err) {
      setError(messageOf(err, "could not save"));
    }
    setBusyId(null);
  }

  if (depleted) {
    return (
      <ProjectDecision
        template={depleted}
        onDecided={() => setDepleted(null)}
      />
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Yesterday and earlier. For each block, say what happened.
      </p>
      {error && <ErrorAlert message={error} />}
      {notice && <p className="text-sm text-destructive">{notice}</p>}
      {lookBack.items.length === 0 ? (
        <p className="text-sm">No events found.</p>
      ) : pending.length === 0 ? (
        <p className="text-sm">All done — you&apos;re all caught up.</p>
      ) : (
        <ul className="space-y-2">
          {pending.map((item) => {
            const label = item.display_label_id
              ? labelById.get(item.display_label_id)
              : undefined;
            const hex = label
              ? labelHex(colors, label)
              : item.color_hex || undefined;
            const busy = busyId === item.id;
            return (
              <li
                key={item.id}
                className="rounded-lg border border-border p-3"
                style={
                  hex ? { borderLeftWidth: 4, borderLeftColor: hex } : undefined
                }
              >
                <div className="flex flex-wrap items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {item.title || "(untitled)"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {when(item)}
                      {label ? ` · ${label.name}` : ""}
                    </p>
                    {item.template && (
                      <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                        <Badge variant="outline">
                          {typeLabel(item.template.type)}
                        </Badge>
                        {item.template.type === "repeating" &&
                          item.template.track_streak && (
                            <span>Streak {item.template.streak}/5</span>
                          )}
                        {item.template.type === "project" && (
                          <span>
                            {formatMinutes(item.template.remaining_minutes)}{" "}
                            left
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <Button
                      size="sm"
                      disabled={busy}
                      onClick={() => void classify(item, "done")}
                    >
                      Done
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => void classify(item, "carry")}
                    >
                      Carry to today
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => void classify(item, "drop")}
                    >
                      Drop
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <div className="flex justify-end">
        <Button
          onClick={onNext}
          disabled={pending.length > 0 || busyId !== null}
        >
          {lookBack.items.length === 0 ? "Skip" : "Next"}
        </Button>
      </div>
    </div>
  );
}

function ProjectDecision({
  template,
  onDecided,
}: {
  template: { id: string; title: string };
  onDecided: () => void;
}) {
  const [minutes, setMinutes] = useState("300");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function decide(decision: "complete" | "keep") {
    setBusy(true);
    setError(null);
    try {
      await api.projectDecision(
        template.id,
        decision,
        decision === "keep" ? Number(minutes) : undefined,
      );
      onDecided();
    } catch (err) {
      setError(messageOf(err, "could not save"));
    }
    setBusy(false);
  }

  return (
    <div className="space-y-4 rounded-lg border border-primary/40 bg-secondary/40 p-4">
      <div>
        <p className="text-sm font-medium">
          &ldquo;{template.title}&rdquo; has no effort left
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          Complete it and remove it from the library, or keep going with more
          effort.
        </p>
      </div>
      {error && <ErrorAlert message={error} />}
      <div className="flex flex-wrap items-end gap-3">
        <Button disabled={busy} onClick={() => void decide("complete")}>
          Complete
        </Button>
        <div className="grid gap-1.5">
          <Label htmlFor="keep-minutes">More effort (minutes)</Label>
          <div className="flex gap-2">
            <Input
              id="keep-minutes"
              className="w-28"
              type="number"
              min={1}
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
            />
            <Button
              variant="outline"
              disabled={busy || Number(minutes) < 1}
              onClick={() => void decide("keep")}
            >
              Keep going
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Step 2: second opinion (optional) ---

/**
 * A pause between the look-back and the score: the user may ask their
 * assistant (connected over MCP) to evaluate the day first. Refresh pulls
 * in an assessment recorded while this step was open.
 */
function SecondOpinionStep({
  lookBack,
  onRefresh,
  onBack,
  onNext,
}: {
  lookBack: LookBack;
  onRefresh: () => Promise<void>;
  onBack: () => void;
  onNext: () => void;
}) {
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const assessment = lookBack.day_plan.assessment;
  const day = new Date(`${lookBack.reviewed_day}T00:00`).toLocaleDateString(
    [],
    {
      weekday: "long",
      day: "numeric",
      month: "long",
    },
  );

  async function refresh() {
    setRefreshing(true);
    setError(null);
    try {
      await onRefresh();
    } catch (err) {
      setError(messageOf(err, "could not check for an assessment"));
    }
    setRefreshing(false);
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium">Want a second opinion on {day}?</p>
        <p className="text-sm text-muted-foreground">
          Optional. Your look-back is saved. If an assistant has Leno connected
          over MCP, ask it now how the day went, for example &ldquo;How did
          yesterday go?&rdquo;. It can read the day and leave its take here,
          which you will see next to the score.
        </p>
      </div>
      {assessment ? (
        <div className="rounded-lg border border-primary/40 bg-secondary/40 p-3">
          <p className="text-sm font-medium">
            Assessment received
            {assessment.suggested_score !== null && (
              <span className="ml-2 rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">
                suggests {assessment.suggested_score}/10
              </span>
            )}
          </p>
          <p className="mt-1 whitespace-pre-wrap text-sm">
            {assessment.summary}
          </p>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          No assessment for this day yet.
        </p>
      )}
      {error && <ErrorAlert message={error} />}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" onClick={onBack} disabled={refreshing}>
          Back
        </Button>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => void refresh()}
            disabled={refreshing}
          >
            {refreshing ? "Checking…" : "Check for assessment"}
          </Button>
          <Button onClick={onNext} disabled={refreshing}>
            {assessment ? "Continue to scoring" : "Skip, score myself"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// --- Step 3: score yesterday ---

function ScoreStep({
  lookBack,
  tally,
  onBack,
  onNext,
}: {
  lookBack: LookBack;
  tally: Tally;
  onBack: () => void;
  onNext: () => void;
}) {
  const total = tally.done + tally.carry + tally.drop;
  const proposed = propose(tally.done, total);
  const assessment = lookBack.day_plan.assessment;
  // Start from the user's own earlier score, else the assistant's suggestion,
  // else the simple done-share proposal.
  const [score, setScore] = useState<number>(
    lookBack.day_plan.score ?? assessment?.suggested_score ?? proposed ?? 5,
  );
  const [note, setNote] = useState(lookBack.day_plan.diary_note);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const day = new Date(`${lookBack.reviewed_day}T00:00`).toLocaleDateString(
    [],
    {
      weekday: "long",
      day: "numeric",
      month: "long",
    },
  );

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.saveDayReview(lookBack.reviewed_day, score, note);
      onNext();
    } catch (err) {
      setError(messageOf(err, "could not save the score"));
    }
    setBusy(false);
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium">How did {day} go?</p>
        <p className="text-sm text-muted-foreground">
          {total === 0
            ? "Nothing was reviewed, so there is no suggestion. Pick a score that feels right."
            : `${tally.done} done, ${tally.carry} carried, ${tally.drop} dropped. Suggested ${proposed}/10.`}
          {lookBack.day_plan.reviewed &&
            " You already scored this day; saving replaces it."}
        </p>
      </div>
      {assessment ? (
        <div className="rounded-lg border border-primary/40 bg-secondary/40 p-3">
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm font-medium">
              Your assistant&apos;s take
              {assessment.suggested_score !== null && (
                <span className="ml-2 rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">
                  {assessment.suggested_score}/10
                </span>
              )}
            </p>
            <span className="text-xs text-muted-foreground">
              {new Date(assessment.assessed_at).toLocaleString([], {
                day: "numeric",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </div>
          <p className="mt-1 whitespace-pre-wrap text-sm">
            {assessment.summary}
          </p>
          {assessment.suggested_score !== null &&
            assessment.suggested_score !== score && (
              <Button
                size="sm"
                variant="outline"
                className="mt-2"
                onClick={() => setScore(assessment.suggested_score as number)}
              >
                Use {assessment.suggested_score}/10
              </Button>
            )}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          Want a second opinion first? Ask your assistant how yesterday went;
          with Leno connected over MCP it can read the day and leave its take
          here.
        </p>
      )}
      <div className="grid gap-1.5">
        <Label htmlFor="day-score">Day Score</Label>
        <div className="flex items-center gap-3">
          <input
            id="day-score"
            type="range"
            min={0}
            max={10}
            step={1}
            value={score}
            onChange={(e) => setScore(Number(e.target.value))}
            className="flex-1 accent-primary"
          />
          <span className="w-12 text-right text-lg font-semibold tabular-nums">
            {score}/10
          </span>
        </div>
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="diary-note">Diary note (optional)</Label>
        <textarea
          id="diary-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="Anything worth remembering about the day."
          className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      {error && <ErrorAlert message={error} />}
      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack} disabled={busy}>
          Back
        </Button>
        <Button onClick={() => void save()} disabled={busy}>
          Save and continue
        </Button>
      </div>
    </div>
  );
}

// --- Step 4: energy for today ---

function EnergyStep({
  today,
  energyLevels,
  initial,
  onBack,
  onFinished,
}: {
  today: string;
  energyLevels: EnergyLevel[];
  initial: string | null;
  onBack: () => void;
  onFinished: () => Promise<void>;
}) {
  const fallback =
    energyLevels.find((l) => l.is_default)?.id ?? energyLevels[0]?.id ?? null;
  const [picked, setPicked] = useState<string | null>(initial ?? fallback);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function finish() {
    if (!picked) return;
    setBusy(true);
    setError(null);
    try {
      await api.setDayEnergyLevel(today, picked);
      await onFinished();
    } catch (err) {
      setError(messageOf(err, "could not save the energy level"));
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium">How much can today hold?</p>
        <p className="text-sm text-muted-foreground">
          The range is today&apos;s Target Load band, as a share of your waking
          day: inside it the day is well planned.
        </p>
      </div>
      {energyLevels.length === 0 ? (
        <ErrorAlert message="You have no Energy Levels. Add at least one in Settings before planning." />
      ) : (
        <div
          className="grid gap-2 sm:grid-cols-2"
          role="radiogroup"
          aria-label="Energy Level"
        >
          {energyLevels.map((level) => {
            const on = picked === level.id;
            return (
              <button
                key={level.id}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setPicked(level.id)}
                className={cn(
                  "flex items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition-colors",
                  on
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card hover:bg-secondary",
                )}
              >
                <span className="font-medium">
                  {level.name}
                  {level.is_default && (
                    <span
                      className={cn(
                        "ml-2 text-xs",
                        on ? "opacity-80" : "text-muted-foreground",
                      )}
                    >
                      default
                    </span>
                  )}
                </span>
                <span className="tabular-nums">{level.range}</span>
              </button>
            );
          })}
        </div>
      )}
      {error && <ErrorAlert message={error} />}
      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack} disabled={busy}>
          Back
        </Button>
        <Button onClick={() => void finish()} disabled={busy || !picked}>
          Finish and plan today
        </Button>
      </div>
    </div>
  );
}
